/* Featured reviews (Fera) — assets/fera-reviews.js
 * Reads a group config from a [data-fera-reviews] element (written by
 * sections/fera-featured-reviews.liquid), fetches matching reviews from Fera's
 * storefront API (window.fera, loaded by the Fera app embed) and renders them.
 * Only approved reviews come back from Fera, so curation there is the source
 * of truth. Nothing is stored in the theme.
 */
(function () {
  'use strict';

  var CACHE_TTL = 20 * 60 * 1000;      // sessionStorage cache per group config
  var PAGE_SIZE = 100;                 // Fera max
  var IDS_PER_CALL = 40;               // keeps the GET query string short
  var STORE_PAGES = 3;                 // how deep to dig for store-level reviews
  var FERA_WAIT_MS = 12000;

  var STAR = '<svg class="icon-star" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 0.5L10.0784 5.63932L15.6085 6.02786L11.3629 9.59268L12.7023 14.9721L8 12.036L3.29772 14.9721L4.63706 9.59268L0.391548 6.02786L5.92159 5.63932L8 0.5Z"></path></svg>';
  var ARROW = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var VERIFIED = '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M8 0l2 2.2 2.9-.5.5 2.9L16 6l-1.6 2.5.7 2.9-2.9.9-1.3 2.7L8 13.8 5.1 15l-1.3-2.7-2.9-.9.7-2.9L0 6l2.6-1.4.5-2.9 2.9.5z" fill="currentColor"/><path d="M4.5 8.2l2.2 2.1 4.6-4.6" stroke="#fff" stroke-width="1.6" fill="none"/></svg>';

  /* ---------- helpers ---------- */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function decode(s) { // Fera bodies arrive HTML-escaped ("&amp;")
    var t = document.createElement('textarea'); t.innerHTML = String(s == null ? '' : s); return t.value;
  }
  function list(s) { return String(s || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean); }
  function num(s, d) { var n = parseInt(s, 10); return isNaN(n) ? d : n; }
  function bool(s) { return s === 'true' || s === true; }
  function unwrap(o) { return (o && o.data) ? o.data : (o || {}); }
  function hash(s) { var h = 0; for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return (h >>> 0).toString(36); }
  function shuffle(a) { for (var i = a.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)); var t = a[i]; a[i] = a[j]; a[j] = t; } return a; }
  function fmtDate(iso) {
    try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }); } catch (e) { return ''; }
  }
  function cacheGet(k) {
    try { var v = JSON.parse(sessionStorage.getItem(k)); if (v && v.t && Date.now() - v.t < CACHE_TTL) return v.d; } catch (e) {}
    return null;
  }
  function cacheSet(k, d) { try { sessionStorage.setItem(k, JSON.stringify({ t: Date.now(), d: d })); } catch (e) {} }

  /* Direct client for Fera's public REST endpoint (same one fera.js calls).
     Works without fera.js — e.g. inside the theme editor, where app embeds
     often don't load. Pass STRING urls to fetch: some storefront apps patch
     window.fetch and choke on URL objects. */
  var REST = 'https://cdn.fera.ai/api/v3/public/reviews.json';
  function restApi(key) {
    return {
      listReviews: function (params) {
        var qs = ['api_key=' + encodeURIComponent(key), 'client=theme-fera-reviews'];
        Object.keys(params || {}).forEach(function (k) {
          var v = params[k]; if (v == null) return;
          if (Object.prototype.toString.call(v) === '[object Array]') v = v.join(',');
          qs.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
        });
        return fetch(REST + '?' + qs.join('&'), { credentials: 'omit' })
          .then(function (r) { if (!r.ok) throw new Error('Fera API ' + r.status); return r.json(); })
          .then(function (j) { return { reviews: (j && (j.data || j.reviews)) || [], meta: (j && j.meta) || {} }; });
      }
    };
  }

  function getApi(cfg) {
    if (cfg.apiKey) return Promise.resolve(restApi(cfg.apiKey));
    return waitForFera();
  }

  function waitForFera() {
    return new Promise(function (resolve, reject) {
      var start = Date.now();
      (function tick() {
        if (window.fera && window.fera.api && typeof window.fera.api.listReviews === 'function') return resolve(window.fera.api);
        if (Date.now() - start > FERA_WAIT_MS) return reject(new Error('Fera did not load'));
        setTimeout(tick, 150);
      })();
    });
  }

  /* ---------- normalise a Fera review into a plain object ---------- */

  function normalise(rv) {
    var d = unwrap(rv), p = unwrap(rv.product), c = unwrap(rv.customer);
    var media = (rv.media || []).map(function (m) {
      var md = unwrap(m);
      return { type: md.type || 'photo', url: md.url || null, thumb: md.thumbnail_url || md.url || null };
    }).filter(function (m) { return m.url; });
    return {
      id: rv.id || d.id,
      subject: d.subject || (d.external_product_id ? 'product' : 'store'),
      rating: Number(d.rating) || 0,
      heading: decode(d.heading || ''),
      body: decode(d.body || ''),
      created_at: d.created_at || '',
      verified: !!d.is_verified,
      product_id: d.external_product_id ? String(d.external_product_id) : '',
      product_name: decode(p.name || ''),
      product_handle: p.handle || '',
      product_thumb: p.thumbnail_url || '',
      author: decode(c.display_name || (d.is_anonymous ? 'Anonymous' : '')),
      location: decode(c.display_location || ''),
      media: media
    };
  }

  /* ---------- fetch ---------- */

  function fetchProductReviews(api, ids) {
    var chunks = [];
    for (var i = 0; i < ids.length; i += IDS_PER_CALL) chunks.push(ids.slice(i, i + IDS_PER_CALL));
    return Promise.all(chunks.map(function (chunk) {
      return api.listReviews({ 'product.ids': chunk, page_size: PAGE_SIZE, page: 1 })
        .then(function (r) { return (r && r.reviews) || []; })
        .catch(function () { return []; });
    })).then(function (lists) { return [].concat.apply([], lists); });
  }

  function fetchStoreReviews(api, pages) {
    var calls = [];
    for (var p = 1; p <= pages; p++) calls.push(api.listReviews({ subject: 'store', page_size: PAGE_SIZE, page: p })
      .then(function (r) { return (r && r.reviews) || []; }).catch(function () { return []; }));
    return Promise.all(calls).then(function (lists) { return [].concat.apply([], lists); });
  }

  function fetchAll(api, cfg) {
    var jobs = [];
    if (cfg.subject !== 'store' && cfg.ids.length) jobs.push(fetchProductReviews(api, cfg.ids));
    if (cfg.subject !== 'product') jobs.push(fetchStoreReviews(api, STORE_PAGES));
    return Promise.all(jobs).then(function (lists) {
      var seen = {}, out = [];
      [].concat.apply([], lists).forEach(function (rv) {
        var n = normalise(rv);
        if (!n.id || seen[n.id]) return;
        seen[n.id] = 1; out.push(n);
      });
      return out;
    });
  }

  /* ---------- filter / order ---------- */

  function select(reviews, cfg) {
    var kws = cfg.keywords.map(function (k) { return k.toLowerCase(); });
    var excl = {}; cfg.exclude.forEach(function (id) { excl[id] = 1; });
    var wanted = {}; cfg.ids.forEach(function (id) { wanted[id] = 1; });

    var pool = reviews.filter(function (r) {
      if (r.rating < cfg.minRating) return false;
      if (r.product_id && excl[r.product_id]) return false;
      if (r.subject === 'product' && cfg.subject === 'store') return false;
      if (r.subject === 'store' && cfg.subject === 'product') return false;
      if (r.subject === 'product' && cfg.ids.length && !wanted[r.product_id]) return false;
      var text = (r.heading + ' ' + r.body).trim();
      if (cfg.minBody > 0 && r.body.trim().length < cfg.minBody) return false;
      if (cfg.minBody === 0 && !text) return false;
      if (kws.length) {
        var lt = text.toLowerCase();
        if (!kws.some(function (k) { return lt.indexOf(k) !== -1; })) return false;
      }
      return true;
    });

    // newest first; photo reviews float to the top when photos_first
    pool.sort(function (a, b) {
      if (cfg.photosFirst) {
        var pa = a.media.length ? 1 : 0, pb = b.media.length ? 1 : 0;
        if (pa !== pb) return pb - pa;
      }
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
    if (cfg.shuffle) {
      // shuffle inside each tier so photo reviews still lead
      var withP = shuffle(pool.filter(function (r) { return r.media.length; }));
      var noP = shuffle(pool.filter(function (r) { return !r.media.length; }));
      pool = cfg.photosFirst ? withP.concat(noP) : shuffle(withP.concat(noP));
    }

    var perProduct = {}, seenText = {}, out = [];
    for (var i = 0; i < pool.length && out.length < cfg.max; i++) {
      var r = pool[i], key = r.product_id || '_store';
      // one customer pasting the same text on many products counts once
      var sig = (r.author + '|' + r.body.trim().toLowerCase()).slice(0, 200);
      if (r.body.trim() && seenText[sig]) continue;
      seenText[sig] = 1;
      if (cfg.cap > 0 && r.product_id) {
        perProduct[key] = (perProduct[key] || 0) + 1;
        if (perProduct[key] > cfg.cap) continue;
      }
      out.push(r);
    }
    return out;
  }

  /* ---------- render ---------- */

  function stars(n) {
    var s = '<div class="testimonial-card-rating fera-review__stars" aria-label="' + n + ' out of 5 stars">';
    for (var i = 1; i <= 5; i++) s += STAR.replace('class="icon-star"', 'class="icon-star' + (i <= n ? ' active' : '') + '"');
    return s + '</div>';
  }

  function card(r, cfg) {
    var body = r.body, trimmed = false;
    if (cfg.truncate > 0 && body.length > cfg.truncate) {
      var cut = body.lastIndexOf(' ', cfg.truncate); if (cut < cfg.truncate * 0.6) cut = cfg.truncate;
      body = body.slice(0, cut).replace(/[\s,.;:-]+$/, ''); trimmed = true;
    }
    var h = '<article class="testimonial-card fera-review' + (r.media.length ? ' fera-review--has-photo' : '') + '" data-review-id="' + esc(r.id) + '">';
    if (r.media.length) {
      var many = r.media.length > 3;
      h += '<div class="fera-review__photos' + (many ? ' fera-review__photos--many' : '') + '" data-fera-photos>';
      if (many) h += '<button type="button" class="fera-review__nav fera-review__nav--prev" data-fera-nav="-1" aria-label="Previous photos" disabled>' + ARROW + '</button>';
      h += '<div class="fera-review__strip" data-fera-strip>';
      r.media.forEach(function (m, idx) {
        h += '<a class="fera-review__photo" href="' + esc(m.url) + '" data-fera-photo="' + idx + '" aria-label="Open photo ' + (idx + 1) + ' of ' + r.media.length + '">' +
             '<img src="' + esc(m.thumb) + '" alt="Customer photo' + (r.product_name ? ' of ' + esc(r.product_name) : '') + '" loading="lazy" decoding="async"></a>';
      });
      h += '</div>';
      if (many) h += '<button type="button" class="fera-review__nav fera-review__nav--next" data-fera-nav="1" aria-label="Next photos">' + ARROW + '</button>' +
                     '<span class="fera-review__count">1–3 / ' + r.media.length + '</span>';
      h += '</div>';
    }
    h += '<div class="testimonial-card-info fera-review__info">';
    h += stars(r.rating);
    if (r.heading) h += '<div class="testimonial-card-author heading-font h6 fera-review__heading">' + esc(r.heading) + '</div>';
    if (r.body) {
      h += '<p class="testimonial-card-text fera-review__body">' + esc(body) +
           (trimmed ? '<span class="fera-review__ellipsis">…</span> <button type="button" class="fera-review__toggle" data-fera-toggle data-full="' + esc(r.body) + '">Read more</button>' : '') + '</p>';
    }
    h += '<div class="fera-review__meta">';
    h += '<span class="fera-review__author">' + esc(r.author || 'Customer') + (r.location ? '<span class="fera-review__location">, ' + esc(r.location) + '</span>' : '') + '</span>';
    if (r.verified) h += '<span class="fera-review__verified" title="Verified buyer">' + VERIFIED + ' Verified</span>';
    if (cfg.showDate && r.created_at) h += '<time class="fera-review__date" datetime="' + esc(r.created_at) + '">' + esc(fmtDate(r.created_at)) + '</time>';
    h += '</div></div>';
    if (cfg.showProduct && r.product_id && r.product_name) {
      var url = r.product_handle ? '/products/' + esc(r.product_handle) : '';
      h += '<div class="testimonial-card-product fera-review__product' + (r.product_thumb ? '' : ' fera-review__product--no-media') + '">' +
           (r.product_thumb ? (url ? '<a href="' + url + '" class="testimonial-card-product-media">' : '<div class="testimonial-card-product-media">') +
             '<div class="media-card media-adapt" style="--imageRatio:1/1"><img src="' + esc(r.product_thumb) + '" alt="" loading="lazy" decoding="async"></div>' +
             (url ? '</a>' : '</div>') : '') +
           '<div class="testimonial-card-product-info">' +
           (url ? '<a href="' + url + '" class="testimonial-card-product-title heading-font text">' : '<div class="testimonial-card-product-title heading-font text">') +
           esc(r.product_name) + (url ? '</a>' : '</div>') + '</div></div>';
    }
    return h + '</article>';
  }

  function render(root, reviews, cfg) {
    var section = root.closest('.shopify-section') || root.closest('section') || root;
    if (!reviews.length) {
      root.innerHTML = '';
      root.setAttribute('data-state', 'empty');
      var design = !!(window.Shopify && window.Shopify.designMode);
      if (cfg.hideEmpty && !design) section.hidden = true;
      else if (design) root.innerHTML = '<p class="fera-reviews-empty">No Fera reviews match this group yet.</p>';
      return;
    }
    section.hidden = false;
    root.innerHTML = '<div class="fera-reviews__track">' + reviews.map(function (r) { return card(r, cfg); }).join('') + '</div>';
    root.setAttribute('data-state', 'ready');
    root.setAttribute('data-count', reviews.length);
    if (cfg.autoplay) autoplay(root, cfg);

    root.addEventListener('click', function (e) {
      var photo = e.target.closest('[data-fera-photo]');
      if (photo) {
        e.preventDefault();
        var strip = photo.closest('[data-fera-strip]');
        var urls = [].map.call(strip.querySelectorAll('[data-fera-photo]'), function (a) { return a.getAttribute('href'); });
        var cardEl = photo.closest('.fera-review');
        var who = cardEl ? (cardEl.querySelector('.fera-review__author') || {}).textContent || '' : '';
        openLightbox(urls, Number(photo.getAttribute('data-fera-photo')) || 0, who.trim());
        return;
      }
      var nav = e.target.closest('[data-fera-nav]');
      if (nav) {
        e.preventDefault();
        var wrap = nav.closest('[data-fera-photos]'), strip = wrap.querySelector('[data-fera-strip]');
        var tile = strip.firstElementChild ? strip.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(strip).columnGap || 8) : 90;
        strip.scrollBy({ left: tile * 3 * Number(nav.getAttribute('data-fera-nav')), behavior: 'smooth' });
        return;
      }
      var t = e.target.closest('[data-fera-toggle]');
      if (!t) return;
      var p = t.parentNode;
      p.textContent = t.getAttribute('data-full');
    });
    // arrow state + "n–m / total" counter follow the strip's scroll position
    root.querySelectorAll('.fera-review__photos--many').forEach(function (wrap) {
      var strip = wrap.querySelector('[data-fera-strip]'), prev = wrap.querySelector('[data-fera-nav="-1"]'), next = wrap.querySelector('[data-fera-nav="1"]'), count = wrap.querySelector('.fera-review__count');
      var total = strip.children.length, ticking = false;
      function update() {
        ticking = false;
        var tile = strip.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(strip).columnGap || 8);
        var first = Math.round(strip.scrollLeft / tile);
        var atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
        prev.disabled = strip.scrollLeft <= 2;
        next.disabled = atEnd;
        if (count) count.textContent = (first + 1) + '–' + Math.min(first + 3, total) + ' / ' + total;
      }
      strip.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      update();
    });
  }

  /* ---------- lightbox: in-page popup for review photos ---------- */
  var CLOSE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  var lb = null;
  function openLightbox(urls, index, caption) {
    if (!urls.length) return;
    closeLightbox();
    var box = document.createElement('div');
    box.className = 'fera-lightbox';
    box.setAttribute('role', 'dialog'); box.setAttribute('aria-modal', 'true'); box.setAttribute('aria-label', 'Customer photo');
    box.innerHTML = '<img class="fera-lightbox__img" alt="Customer photo">' +
      '<button type="button" class="fera-lightbox__btn fera-lightbox__close" data-lb="close" aria-label="Close">' + CLOSE + '</button>' +
      '<button type="button" class="fera-lightbox__btn fera-lightbox__prev" data-lb="-1" aria-label="Previous photo">' + ARROW + '</button>' +
      '<button type="button" class="fera-lightbox__btn fera-lightbox__next" data-lb="1" aria-label="Next photo">' + ARROW + '</button>' +
      '<div class="fera-lightbox__caption"></div>';
    document.body.appendChild(box);
    document.body.classList.add('fera-lightbox-open');
    lb = { box: box, urls: urls, i: index, caption: caption, opener: document.activeElement,
           onKey: function (e) { if (e.key === 'Escape') closeLightbox(); else if (e.key === 'ArrowRight') show(lb.i + 1); else if (e.key === 'ArrowLeft') show(lb.i - 1); } };
    document.addEventListener('keydown', lb.onKey);
    box.addEventListener('click', function (e) {
      var b = e.target.closest('[data-lb]');
      if (b) { var v = b.getAttribute('data-lb'); if (v === 'close') closeLightbox(); else show(lb.i + Number(v)); return; }
      if (e.target === box) closeLightbox();
    });
    var sx = 0;
    box.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', function (e) { var dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 40) show(lb.i + (dx < 0 ? 1 : -1)); }, { passive: true });
    function show(i) {
      if (!lb) return;
      if (i < 0 || i >= lb.urls.length) return;
      lb.i = i;
      var img = box.querySelector('.fera-lightbox__img');
      img.src = lb.urls[i];
      box.querySelector('.fera-lightbox__prev').hidden = i === 0;
      box.querySelector('.fera-lightbox__next').hidden = i === lb.urls.length - 1;
      box.querySelector('.fera-lightbox__caption').textContent = (lb.caption ? lb.caption + ' · ' : '') + (i + 1) + ' / ' + lb.urls.length;
    }
    show(index);
    box.querySelector('.fera-lightbox__close').focus();
  }
  function closeLightbox() {
    if (!lb) return;
    document.removeEventListener('keydown', lb.onKey);
    lb.box.parentNode && lb.box.parentNode.removeChild(lb.box);
    document.body.classList.remove('fera-lightbox-open');
    if (lb.opener && lb.opener.focus) { try { lb.opener.focus(); } catch (e) {} }
    lb = null;
  }

  /* ---------- autoplay: continuous right-to-left glide, seamless loop ----------
     The track's cards are cloned once so the row can wrap without a jump;
     scrollLeft advances by speed px/s and is pulled back by one set's width
     when it passes it. Pauses on hover / touch / focus; off under
     prefers-reduced-motion. */
  function autoplay(root, cfg) {
    var track = root.querySelector('.fera-reviews__track');
    if (!track || track.children.length < 2) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (root.__feraAuto) { cancelAnimationFrame(root.__feraAuto.raf); root.__feraAuto = null; }

    var originals = [].slice.call(track.children);
    function setWidth() {
      var last = originals[originals.length - 1];
      return last.offsetLeft + last.offsetWidth + parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 20) - originals[0].offsetLeft;
    }
    if (track.scrollWidth <= track.clientWidth + 4) return;   // nothing to rotate
    originals.forEach(function (el) { var c = el.cloneNode(true); c.setAttribute('aria-hidden', 'true'); c.setAttribute('data-clone', ''); track.appendChild(c); });

    var state = { paused: false, last: null, raf: 0, hold: 0 };
    root.__feraAuto = state;
    var pause = function () { state.paused = true; };
    var resume = function () { state.paused = false; state.last = null; };
    root.addEventListener('mouseenter', pause); root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause); root.addEventListener('focusout', resume);
    track.addEventListener('touchstart', function () { state.paused = true; state.hold = Date.now(); }, { passive: true });
    track.addEventListener('touchend', function () { setTimeout(function () { if (Date.now() - state.hold >= 2500) resume(); }, 2600); state.hold = Date.now(); }, { passive: true });
    document.addEventListener('visibilitychange', function () { state.last = null; });

    function step(ts) {
      state.raf = requestAnimationFrame(step);
      if (state.paused || document.hidden) { state.last = ts; return; }
      if (state.last == null) { state.last = ts; return; }
      var dt = Math.min(64, ts - state.last); state.last = ts;
      var w = setWidth();
      var next = track.scrollLeft + cfg.speed * dt / 1000;
      if (next >= w) next -= w;
      track.scrollLeft = next;
    }
    state.raf = requestAnimationFrame(step);
  }

  /* ---------- boot ---------- */

  function readConfig(root) {
    var d = root.dataset;
    return {
      ids: list(d.ids).filter(function (v, i, a) { return a.indexOf(v) === i; }),
      exclude: list(d.excludeIds),
      subject: d.subject || 'product',
      minRating: num(d.minRating, 4),
      minBody: num(d.minBody, 60),
      cap: num(d.cap, 3),
      photosFirst: bool(d.photosFirst),
      keywords: list(d.keywords),
      max: Math.max(1, num(d.max, 8)),
      shuffle: bool(d.shuffle),
      showProduct: bool(d.showProduct),
      showDate: bool(d.showDate),
      truncate: num(d.truncate, 240),
      hideEmpty: bool(d.hideEmpty),
      apiKey: (d.apiKey || '').trim(),
      autoplay: bool(d.autoplay),
      speed: Math.max(5, num(d.speed, 40)),
      group: d.group || 'none'
    };
  }

  function init(root) {
    if (root.__feraInit) return; root.__feraInit = true;
    var cfg = readConfig(root);
    if (cfg.subject !== 'store' && !cfg.ids.length && cfg.subject === 'product') { render(root, [], cfg); return; }
    var key = 'fera-reviews:' + cfg.group + ':' + hash(JSON.stringify([cfg.ids, cfg.subject]));
    var cached = cacheGet(key);
    var start = cached ? Promise.resolve(cached) : getApi(cfg).then(function (api) {
      return fetchAll(api, cfg).then(function (all) { cacheSet(key, all); return all; });
    });
    start.then(function (all) { render(root, select(all, cfg), cfg); })
      .catch(function (err) {
        console.warn('[fera-reviews]', err && err.message ? err.message : err);
        render(root, [], cfg);
      });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-fera-reviews]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector && e.target.querySelector('[data-fera-reviews]');
    if (el) { el.__feraInit = false; init(el); }
  });

  window.FeraFeaturedReviews = { boot: boot, select: select, normalise: normalise };
})();
